import {LINK_TILE_ICONS} from "../entities/link-tile-icon.const";

/**
 * The icon keys a Linkboard can carry in the sidebar — DERIVED from the one
 * shared icon list (`LINK_TILE_ICONS`, Anke 2026-09-20: "eine gemeinsame Liste
 * für Kacheln UND Boards"), never a second hand-maintained list. Whatever is
 * added there is offered here, in the same order.
 *
 * WHY THIS HELPER STILL EXISTS next to the list: the SIDEBAR does not render
 * the glyph from `codepoint` the way `ha-input-icon` does. A row is an `<a>`
 * with `data-icon="<key>"` and the glyph comes from a
 * `[data-icon="<key>"]::before` rule in
 * `backend/public/assets/css/linkview_sidebar.css` (house convention, same mechanism as
 * the todo entries), so the sidebar only ever needs the KEY — plus a
 * guarantee that the key it puts on the element has a rule to match.
 * `tests/domain/helpers/linkview-icons.helper.test.ts` reads the stylesheets and
 * fails when an entry of the shared list has no rule there (or its `content`
 * is not that entry's `codepoint`), so list and stylesheet cannot drift.
 *
 * The key is what the backend stores (`Linkview.icon`); a board created before
 * the field existed — or one saved with no icon chosen — carries `null`.
 */
export const LINKVIEW_ICON_KEYS: readonly string[] = LINK_TILE_ICONS.map((icon) => icon.id);

/**
 * Just `string` since the keys come from a runtime list rather than a literal
 * tuple. The compile-time narrowing the old literal union gave was never worth
 * a second, hand-kept copy of the icons — `resolveLinkviewIcon` is the runtime
 * check that actually protects the sidebar.
 */
export type LinkviewIconKey = string;

/** Preselected in the create dialog, and what a missing/unknown key falls back to. */
export const DEFAULT_LINKVIEW_ICON: LinkviewIconKey = 'link';

/**
 * Maps whatever the storage/backend handed over to a key the stylesheets have a rule
 * for: `null`/`undefined` (a board from before the field existed, or one whose
 * icon was deselected in the dialog), an empty string, or a key from a
 * newer/older client all become `link`, so a row never renders without a glyph.
 */
export function resolveLinkviewIcon(icon: string | null | undefined): LinkviewIconKey {
    return LINKVIEW_ICON_KEYS.includes(icon ?? '')
        ? icon as LinkviewIconKey
        : DEFAULT_LINKVIEW_ICON;
}
